import { useCallback, useContext, useMemo } from 'react';
import type { FormattedMemberTransaction } from '@library/types/members/memberTransactions';
import { LinkButton } from '@library/components/buttons';
import { MemberTransactionsTableContext } from '@bo-hooks/members';
import { getDollarAmountDisplay } from '@bo-utils/members/memberCommon';

export interface MemberTransactionsTableRowProps {
  memberTransaction: FormattedMemberTransaction;
}

export const MemberTransactionsTableRow = ({
  memberTransaction
}: MemberTransactionsTableRowProps) => {
  const {
    createdAtDate,
    createdAtTime,
    creditAtTimeAmount,
    promoId,
    newWagerId,
    promoAtTimeAmount,
    reason,
    transactionAmount,
    transactionId,
    transactionType
  } = memberTransaction;

  const {
    memberTransactionTypeOptions,
    onClickPromotionId,
    onClickNewWagerId
  } = useContext(MemberTransactionsTableContext);

  const transactionAmountDisplay = useMemo(
    () => getDollarAmountDisplay({ dollarAmount: transactionAmount }),
    [transactionAmount]
  );

  const creditAtTimeAmountDisplay = useMemo(
    () => getDollarAmountDisplay({ dollarAmount: creditAtTimeAmount }),
    [creditAtTimeAmount]
  );

  const promoAtTimeAmountDisplay = useMemo(
    () => getDollarAmountDisplay({ dollarAmount: promoAtTimeAmount }),
    [promoAtTimeAmount]
  );

  const transactionTypeDisplay = useMemo(() => {
    const transactionTypeOption = memberTransactionTypeOptions.find(
      (option) => (option.value as string) === transactionType
    );

    return transactionTypeOption?.label || transactionType;
  }, [transactionType, memberTransactionTypeOptions]);

  const handleClickPromotionId = useCallback(
    () => onClickPromotionId({ promotionId: promoId }),
    [onClickPromotionId, promoId]
  );

  const handleClickNewWagerId = useCallback(
    () => onClickNewWagerId({ entryId: newWagerId }),
    [onClickNewWagerId, newWagerId]
  );

  return (
    <div
      className="border-soFresh-140 text-soClean-110 hover:bg-soFresh-120 text-2xs relative flex h-full min-h-16 cursor-default items-center border-b xl:text-xs"
      data-testid={`member-transaction-${transactionId}`}
    >
      <p className="w-[10%] px-3" data-testid="member-transaction-Id">
        {transactionId}
      </p>
      <p className="w-[10%] px-3" data-testid="member-transaction-amount">
        {transactionAmountDisplay}
      </p>
      <p className="w-1/5 px-3" data-testid="member-transaction-type">
        {transactionTypeDisplay}
      </p>
      <p
        className="w-[10%] px-3"
        data-testid="member-transaction-creaditAtTime"
      >
        {creditAtTimeAmountDisplay}
      </p>
      <p className="w-[10%] px-3" data-testid="member-transaction-promoAtTime">
        {promoAtTimeAmountDisplay}
      </p>
      <p
        className="flex w-[15%] flex-col px-3"
        data-testid="member-transaction-createdAt"
      >
        <span
          className="leading-5"
          data-testid="member-transaction-createdAtDate"
        >
          {createdAtDate}
        </span>
        <span
          className="text-soClean-160"
          data-testid="member-transaction-createdAtTime"
        >
          {createdAtTime}
        </span>
      </p>
      <p className="w-[10%] px-3" data-testid="member-transaction-promoId">
        {promoId && (
          <LinkButton
            className="text-2xs xl:text-xs"
            label={promoId}
            onClick={handleClickPromotionId}
          />
        )}
        {newWagerId && (
          <LinkButton
            className="text-2xs xl:text-xs"
            label={newWagerId}
            onClick={handleClickNewWagerId}
          />
        )}
      </p>
      <p className="w-[15%] px-3" data-testid="member-transaction-reason">
        {reason}
      </p>
    </div>
  );
};
